import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type PlatformRow = {
  label: string
  instagram: string
  facebook: string
  tiktok: string
}

const AUDIENCE_DATA: PlatformRow[] = [
  {
    label: "UK monthly active users",
    instagram: "~35 million",
    facebook: "~45 million",
    tiktok: "~25 million",
  },
  {
    label: "Primary age demographic",
    instagram: "25-44",
    facebook: "30-55+",
    tiktok: "18-34",
  },
  {
    label: "Homeowner skew",
    instagram: "Moderate (aspirational buyers)",
    facebook: "Strong (established homeowners)",
    tiktok: "Lower (first-time buyers, renters)",
  },
  {
    label: "Peak property browsing time",
    instagram: "7-9pm weekdays, Sunday AM",
    facebook: "12-2pm, 7-9pm weekdays",
    tiktok: "7-11pm weekdays, weekends",
  },
  {
    label: "Content discovery model",
    instagram: "Followers + Explore + Reels",
    facebook: "Friends + Groups + Marketplace",
    tiktok: "For You Page (algorithm-first)",
  },
]

const FORMAT_DATA: PlatformRow[] = [
  {
    label: "Best content format",
    instagram: "Reels (9:16 video, 15-60s)",
    facebook: "Short video + carousel posts",
    tiktok: "Short video (9:16, 15-60s)",
  },
  {
    label: "Optimal video length",
    instagram: "15-30 seconds for Reels",
    facebook: "30-60 seconds for feed",
    tiktok: "15-30 seconds",
  },
  {
    label: "Image carousels",
    instagram: "Up to 20 slides, high engagement",
    facebook: "Up to 10 images, moderate reach",
    tiktok: "Photo carousel (newer feature)",
  },
  {
    label: "Stories / ephemeral content",
    instagram: "Stories (24h), Highlights",
    facebook: "Stories (lower engagement)",
    tiktok: "Stories (limited adoption)",
  },
  {
    label: "Live streaming",
    instagram: "Instagram Live (good for Q&A)",
    facebook: "Facebook Live (strongest reach)",
    tiktok: "TikTok Live (1,000+ followers)",
  },
  {
    label: "Link in post",
    instagram: "No (link in bio only)",
    facebook: "Yes (direct URL in posts)",
    tiktok: "No (link in bio, 1,000+ followers)",
  },
]

const ADVERTISING_DATA: PlatformRow[] = [
  {
    label: "Ad platform",
    instagram: "Meta Ads Manager",
    facebook: "Meta Ads Manager",
    tiktok: "TikTok Ads Manager",
  },
  {
    label: "Minimum daily budget",
    instagram: "£1/day",
    facebook: "£1/day",
    tiktok: "£20/day (campaign level)",
  },
  {
    label: "Typical CPM (cost per 1,000 views)",
    instagram: "£4-8",
    facebook: "£3-7",
    tiktok: "£2-5",
  },
  {
    label: "Geographic targeting",
    instagram: "Postcode, radius, city",
    facebook: "Postcode, radius, city, county",
    tiktok: "City, region (less granular)",
  },
  {
    label: "Interest targeting",
    instagram: "Property, home improvement, moving",
    facebook: "Property, likely to move, life events",
    tiktok: "Property, home, interior design",
  },
  {
    label: "Retargeting",
    instagram: "Website visitors, engagement, lists",
    facebook: "Website visitors, engagement, lists",
    tiktok: "Website visitors, engagement",
  },
  {
    label: "Lookalike audiences",
    instagram: "Yes (via Meta)",
    facebook: "Yes (strongest data set)",
    tiktok: "Yes (growing capability)",
  },
  {
    label: "Best for property ads",
    instagram: "Brand building + local reach",
    facebook: "Targeted lead gen + vendor reach",
    tiktok: "Viral reach + younger audience",
  },
]

const PLATFORM_STATS = [
  {
    value: "3.2x",
    label: "more enquiries with video",
    detail:
      "Property listings promoted with video on social media generate over three times more enquiries than static images alone.",
  },
  {
    value: "78%",
    label: "of agents use social",
    detail:
      "The vast majority of UK estate agents now use at least one social platform for marketing. Video adoption is still catching up.",
  },
  {
    value: "£5-15",
    label: "cost per property enquiry",
    detail:
      "Well-targeted property video ads on social media typically generate enquiries at a fraction of the cost of traditional advertising.",
  },
  {
    value: "60s",
    label: "to create a listing video",
    detail:
      "With QuiteRight, agents create professional property videos in under a minute, ready for all three platforms.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Which social platform should I focus on for property marketing?",
    a: "For most estate agents, Facebook is the strongest starting point. It has the largest UK audience of established homeowners and the most powerful targeting tools for reaching people likely to move. Add Instagram for brand building and a younger buyer audience. TikTok is valuable if you want to reach first-time buyers and build viral awareness.",
  },
  {
    q: "How much should I spend on social media advertising for listings?",
    a: "A practical starting budget is £100-300 per month, split across Facebook and Instagram. This is enough to promote 10-15 listings with targeted local ads. As you see results, increase spend on the platform that generates the best cost per enquiry. Many agents find £5-15 per enquiry is achievable with good video content.",
  },
  {
    q: "Should I post the same content on all three platforms?",
    a: "You can repurpose the same video across all three, but tailor the caption and format. Instagram and TikTok favour vertical 9:16 video with punchy captions. Facebook works well with slightly longer video and more descriptive text. QuiteRight exports in all the right formats automatically.",
  },
  {
    q: "Is TikTok actually useful for estate agents?",
    a: "Yes, particularly for reaching first-time buyers and renters. TikTok's algorithm surfaces content to people who have never heard of your agency, which makes it excellent for brand awareness. Property tour videos regularly go viral on TikTok. The audience skews younger, so it complements rather than replaces Facebook.",
  },
  {
    q: "What type of property content performs best on social media?",
    a: "Short property tour videos (15-30 seconds) consistently outperform static images across all three platforms. Behind-the-scenes content, local area guides, market updates, and sold/let announcements also perform well. The key is consistency: agents who post 3-5 times per week see significantly better results than those who post sporadically.",
  },
  {
    q: "Can I boost listings directly from my business page?",
    a: "Yes, on both Facebook and Instagram you can boost posts directly from your business page. However, using Meta Ads Manager gives you better targeting options, more placement control, and lower costs. For TikTok, you will need TikTok Ads Manager. QuiteRight can publish directly to all three platforms.",
  },
  {
    q: "How do I measure ROI from social media property marketing?",
    a: "Track three metrics: reach (how many people saw your listing), engagement (clicks, saves, shares), and enquiries (messages, calls, website visits). Use UTM parameters on links to track which platform drives traffic to your listings. Over time, calculate your cost per enquiry and cost per viewing for each platform.",
  },
  {
    q: "Do I need separate video for each platform?",
    a: "Ideally, yes, as each platform has different optimal dimensions and lengths. In practice, vertical 9:16 video works on all three (Reels, TikTok, and Facebook Reels/Stories). QuiteRight creates video in multiple formats from a single listing, so you do not need to edit separately for each platform.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function PlatformBadge({
  name,
  colour,
}: {
  name: string
  colour: string
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white"
      style={{ backgroundColor: colour }}
    >
      {name}
    </span>
  )
}

function PlatformComparisonTable({
  rows,
  title,
}: {
  rows: PlatformRow[]
  title: string
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[700px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              {title}
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PlatformBadge name="Instagram" colour="#E1306C" />
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PlatformBadge name="Facebook" colour="#1877F2" />
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PlatformBadge name="TikTok" colour="#1A1A1A" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.instagram}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.facebook}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm text-slate">
                {row.tiktok}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function InstagramVsFacebookVsTiktok({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Instagram vs Facebook vs TikTok for Property Advertising: Which Platform Wins?",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Instagram vs Facebook vs TikTok for Property Ads" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Social media comparison 2026
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Instagram vs Facebook vs TikTok for property ads
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Three platforms, three audiences, three strategies. This guide
            compares Instagram, Facebook, and TikTok for UK estate agents who
            want to advertise listings where buyers actually spend their time.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <PlatformBadge name="Instagram" colour="#E1306C" />
            <PlatformBadge name="Facebook" colour="#1877F2" />
            <PlatformBadge name="TikTok" colour="#1A1A1A" />
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The opportunity
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Why social media matters for listings
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PLATFORM_STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Platform deep dives */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Platform profiles
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Each platform at a glance
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {/* Instagram */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PlatformBadge name="Instagram" colour="#E1306C" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The visual showroom
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="text-charcoal underline decoration-stone underline-offset-2 hover:decoration-red">Instagram</a> is where property looks its best. The platform
                  rewards polished visuals, and Reels give listings enormous
                  organic reach. It is the go-to platform for aspirational
                  buyers and for building your agency brand. The 25-44 age
                  group makes up the core audience, covering upsizers,
                  second-time buyers, and affluent professionals.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Best for:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Brand building and visual storytelling
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Reels for organic listing exposure
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Reaching aspirational and upsizing buyers
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Showcasing premium and lifestyle properties
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      No clickable links in organic posts
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Organic reach declining for static posts
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Less effective for reaching over-55s
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Facebook */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <PlatformBadge name="Facebook" colour="#1877F2" />
                  <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Top pick for agents
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The lead generation engine
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="text-charcoal underline decoration-stone underline-offset-2 hover:decoration-red">Facebook</a> remains the most powerful platform for property
                  advertising. With 45 million UK users skewing towards
                  established homeowners, it offers unmatched targeting for
                  reaching people likely to move. <a href="https://www.facebook.com/groups" target="_blank" rel="noopener noreferrer" className="text-charcoal underline decoration-stone underline-offset-2 hover:decoration-red">Facebook Groups</a>, <a href="https://www.facebook.com/marketplace" target="_blank" rel="noopener noreferrer" className="text-charcoal underline decoration-stone underline-offset-2 hover:decoration-red">Marketplace</a>,
                  and <a href="https://www.facebook.com/business/ads" target="_blank" rel="noopener noreferrer" className="text-charcoal underline decoration-stone underline-offset-2 hover:decoration-red">Meta Ads Manager</a> make it the most complete
                  advertising ecosystem for estate agents.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Best for:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Targeted advertising to likely movers
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Reaching homeowners and downsizers (35-65+)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Local community groups for awareness
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Direct links to listings in posts
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Organic reach is very low for business pages
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Younger demographics less active
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Requires ad spend for meaningful reach
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* TikTok */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PlatformBadge name="TikTok" colour="#1A1A1A" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The viral discovery engine
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-charcoal underline decoration-stone underline-offset-2 hover:decoration-red">TikTok</a>&rsquo;s algorithm-first model means your property
                  videos can reach thousands of people who have never heard of
                  your agency. It is the best platform for organic reach in
                  2026. The audience skews younger (18-34), making it ideal for
                  reaching first-time buyers, renters, and the next generation
                  of homeowners.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Best for:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Organic viral reach (no ad spend needed)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Reaching first-time buyers and renters
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Building agency personality and culture
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Property tours that feel authentic
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Less precise geographic targeting for ads
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Smaller UK homeowner audience
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Higher minimum ad spend (£20/day)
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Audience comparison table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Audience
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Who are you reaching?
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Understanding each platform&rsquo;s audience helps you decide
              where to invest your time and budget for different types of
              listings.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <PlatformComparisonTable rows={AUDIENCE_DATA} title="Audience" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Content format table */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Content
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Formats and content types
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Each platform favours different content formats. Here is what
              works best for property listings on each.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <PlatformComparisonTable rows={FORMAT_DATA} title="Format" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Advertising comparison table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Paid advertising
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Advertising tools and costs
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              When organic reach is not enough, paid advertising puts your
              listings in front of the right people. Here is how the
              advertising platforms compare.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <PlatformComparisonTable
                  rows={ADVERTISING_DATA}
                  title="Advertising"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Strategy recommendations */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Recommendations
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Which platform strategy fits your agency?
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Just getting started with social
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Start with Facebook and Instagram (managed together through
                  Meta). Post every new listing as a Reel on Instagram and a
                  short video on Facebook. Boost your best-performing posts with
                  £5-10 each, targeting a 5-mile radius around the property.
                  This requires minimal time and budget while covering the two
                  largest audiences. Our guide to{" "}
                  <Link href="/guides/social-media-estate-agents" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">social media for estate agents</Link>{" "}
                  covers the fundamentals in more detail.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Established agency wanting more reach
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Use all three platforms. Facebook for targeted paid campaigns
                  to likely movers. Instagram for brand building and organic
                  Reels reach. TikTok for viral discovery and reaching the
                  next generation of buyers. Allocate 60% of your ad budget to
                  Facebook, 30% to Instagram, and 10% to TikTok experiments.
                  See our{" "}
                  <Link href="/guides/property-video-marketing" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">property video marketing</Link>{" "}
                  guide for tips on making video work across all three.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Letting agent focused on rentals
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Instagram and TikTok are your strongest channels. Renters
                  skew younger and spend more time on these platforms. Post
                  property tour{" "}
                  <Link href="/guides/instagram-reels-estate-agents" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">Reels on Instagram</Link>{" "}
                  and short{" "}
                  <Link href="/guides/tiktok-estate-agents" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">walkthroughs on TikTok</Link>.
                  Facebook Groups are also valuable for local rental
                  demand. Budget more towards organic content creation than paid
                  advertising.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Premium and rural agent
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Instagram is your primary platform. Premium and lifestyle
                  properties perform exceptionally well on Instagram, where the
                  visual-first format showcases interiors, gardens, and
                  settings. Supplement with Facebook for targeted ads reaching
                  high-net-worth individuals and downsizers. Combine social
                  reach with strong{" "}
                  <Link href="/guides/zoopla-vs-rightmove-vs-onthemarket" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">portal presence on Rightmove, Zoopla, and OnTheMarket</Link>{" "}
                  for maximum exposure. TikTok is optional
                  but can surprise with viral reach for unique properties.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Content calendar */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Practical guide
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              A weekly content plan that works
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Consistency matters more than perfection. Here is a realistic
              posting schedule for a busy estate agent. Tools like{" "}
              <Link href="/how-it-works" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">QuiteRight</Link>{" "}
              make it easy to create videos for every slot.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="overflow-x-auto rounded-xl border border-stone bg-white p-6 md:p-8">
                <table className="w-full min-w-[600px] text-left">
                  <thead>
                    <tr className="border-b-2 border-stone">
                      <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                        Day
                      </th>
                      <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                        Content
                      </th>
                      <th className="pb-3 pl-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                        Platform
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        day: "Monday",
                        content: "New listing video tour",
                        platform: "Instagram Reels + TikTok + Facebook",
                      },
                      {
                        day: "Tuesday",
                        content: "Market update or tip",
                        platform: "Facebook + Instagram Stories",
                      },
                      {
                        day: "Wednesday",
                        content: "Behind the scenes / team content",
                        platform: "Instagram Stories + TikTok",
                      },
                      {
                        day: "Thursday",
                        content: "New listing video or price reduction",
                        platform: "Instagram Reels + Facebook + TikTok",
                      },
                      {
                        day: "Friday",
                        content: "Sold / let announcement",
                        platform: "All three platforms",
                      },
                      {
                        day: "Saturday",
                        content: "Local area highlight or weekend tips",
                        platform: "Instagram + TikTok",
                      },
                      {
                        day: "Sunday",
                        content: "Premium listing showcase",
                        platform: "Instagram Reels + Facebook",
                      },
                    ].map((row, i) => (
                      <tr
                        key={i}
                        className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
                      >
                        <td className="py-3 pr-4 text-sm font-medium text-charcoal">
                          {row.day}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate">
                          {row.content}
                        </td>
                        <td className="py-3 pl-4 text-sm text-slate">
                          {row.platform}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* QuiteRight integration */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-xl border-2 border-red/20 bg-red/5 p-8 md:p-10">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                One video, every platform
              </p>
              <h2 className="mt-4 font-display text-2xl font-bold text-charcoal md:text-3xl">
                Create listing videos for all three platforms in 60 seconds
                <span className="text-red">.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate">
                The biggest barrier to social media marketing is creating
                content. QuiteRight removes that barrier entirely. Paste a
                listing URL from{" "}
                <Link href="/integrations" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">your portal listing</Link>,
                pick a template, and get a professional{" "}
                <Link href="/guides/property-video-script-writing" className="text-red underline decoration-red/30 underline-offset-2 hover:decoration-red">property video</Link>{" "}
                ready for Instagram Reels, Facebook, and TikTok. No
                filming, no editing, no resizing.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {[
                  {
                    title: "Instagram Reels",
                    detail: "9:16 vertical format, 15-30s, optimised for the Explore page",
                  },
                  {
                    title: "Facebook Video",
                    detail: "Multiple formats, captions included, ready for feed or ads",
                  },
                  {
                    title: "TikTok",
                    detail: "Vertical format, punchy pacing, designed for the For You Page",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg bg-white p-4 text-center"
                  >
                    <p className="text-sm font-semibold text-charcoal">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-slate">{item.detail}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/how-it-works"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Related guides
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/guides/instagram-reels-estate-agents",
                title: "Instagram Reels for estate agents",
                desc: "A deep dive into making Reels work for your property listings.",
              },
              {
                href: "/guides/tiktok-estate-agents",
                title: "TikTok for estate agents",
                desc: "How to use TikTok to reach buyers without trending dances.",
              },
              {
                href: "/guides/zoopla-vs-rightmove-vs-onthemarket",
                title: "Zoopla vs Rightmove vs OnTheMarket",
                desc: "Compare the UK's three major property portals side by side.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
              Every listing deserves a video ad
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
              QuiteRight creates professional property videos for Instagram,
              Facebook, and TikTok in under 60 seconds. More views, more
              enquiries, more instructions.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
            <p className="mt-5 text-sm text-warm-grey">
              14-day free trial &middot; No credit card &middot; Cancel any time
            </p>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
